/**
 * Subskill taxonomy.
 *
 * Subskills are the underlying operations a question exercises, tracked
 * separately from the topic so diagnostics can observe correlations like
 * "misses cluster on negative-number operations" independent of which topic
 * the question came from. The taxonomy is deliberately compact: every entry
 * must be something a generator can reliably tag and that produces a useful
 * diagnostic signal. Adding fine-grained skills that cannot be tagged
 * accurately would only create noise.
 */

export const SUBSKILLS = {
  integerArithmetic: "Integer arithmetic",
  negativeNumbers: "Negative number operations",
  fractionArithmetic: "Fraction arithmetic",
  distributive: "Distributive property",
  combiningLikeTerms: "Combining like terms",
  inverseOperations: "Inverse operations",
  variableIsolation: "Variable isolation",
  variablesBothSides: "Variables on both sides",
  crossMultiplication: "Cross multiplication",
  absoluteValue: "Absolute value reasoning",
  inequalityReversal: "Inequality reversal",
  andOrReasoning: "AND/OR reasoning",
  coordinateInterpretation: "Coordinate interpretation",
  domain: "Domain",
  range: "Range",
  functionIdentification: "Function identification",
  substitution: "Substitution",
  functionNotation: "Function notation",
  graphInterpretation: "Graph interpretation",
} as const;

export type SubskillId = keyof typeof SUBSKILLS;

export function subskillLabel(id: SubskillId): string {
  return SUBSKILLS[id];
}

export function isSubskillId(x: string): x is SubskillId {
  return Object.prototype.hasOwnProperty.call(SUBSKILLS, x);
}
